import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import { useSiteSettings } from '../../context/SiteSettingsContext';

export default function AdminSettings() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const { settings: siteSettings, refreshSettings } = useSiteSettings();
  const [activeTab, setActiveTab] = useState('dues');
  const [duesStructures, setDuesStructures] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duesForm, setDuesForm] = useState({ membership_category: 'fellow', year: new Date().getFullYear(), amount: '' });
  const [auditPage, setAuditPage] = useState(1);
  const auditPerPage = 15;

  // App branding state
  const [branding, setBranding] = useState({
    login_bg_type: 'color',
    login_bg_color: '#F8FAFB',
    login_bg_overlay_color: '#000000',
    login_bg_overlay_opacity: 0,
  });
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [bgImagePreview, setBgImagePreview] = useState(null);
  const [bgVideoPreview, setBgVideoPreview] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const bgImageRef = useRef(null);
  const bgVideoRef = useRef(null);
  const logoRef = useRef(null);
  const faviconRef = useRef(null);

  useEffect(() => {
    if (activeTab === 'dues') {
      setLoading(true);
      api.get('/admin/dues-structure/').then(res => setDuesStructures(res.data.results || res.data))
        .finally(() => setLoading(false));
    } else if (activeTab === 'audit') {
      setLoading(true);
      api.get('/accounts/admin/audit-logs/').then(res => { setAuditLogs(res.data.results || res.data); setAuditPage(1); })
        .catch(() => {}).finally(() => setLoading(false));
    } else if (activeTab === 'branding') {
      setLoading(true);
      api.get('/admin/site-settings/').then(res => {
        setBranding({
          login_bg_type: res.data.login_bg_type || 'color',
          login_bg_color: res.data.login_bg_color || '#F8FAFB',
          login_bg_overlay_color: res.data.login_bg_overlay_color || '#000000',
          login_bg_overlay_opacity: res.data.login_bg_overlay_opacity ?? 0,
        });
        setBgImagePreview(res.data.login_bg_image || null);
        setBgVideoPreview(res.data.login_bg_video || null);
        setLogoPreview(res.data.header_logo || null);
        setFaviconPreview(res.data.favicon || null);
      }).catch(() => {}).finally(() => setLoading(false));
    }
  }, [activeTab]);

  const handleFilePreview = (file, setter) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setter(url);
  };

  const handleBrandingDelete = async (field) => {
    if (!window.confirm('Remove this file?')) return;
    try {
      await api.delete(`/admin/site-settings/clear/${field}/`);
      const previewMap = {
        login_bg_image: setBgImagePreview,
        login_bg_video: setBgVideoPreview,
        header_logo: setLogoPreview,
        favicon: setFaviconPreview,
      };
      previewMap[field]?.(null);
      if (field === 'login_bg_image' && bgImageRef.current) bgImageRef.current.value = '';
      if (field === 'login_bg_video' && bgVideoRef.current) bgVideoRef.current.value = '';
      if (field === 'header_logo' && logoRef.current) logoRef.current.value = '';
      if (field === 'favicon' && faviconRef.current) faviconRef.current.value = '';
      toast.success('File removed.');
      refreshSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove file.');
    }
  };

  const handleBrandingSave = async () => {
    setBrandingSaving(true);
    try {
      const formData = new FormData();
      formData.append('login_bg_type', branding.login_bg_type);
      formData.append('login_bg_color', branding.login_bg_color);
      formData.append('login_bg_overlay_color', branding.login_bg_overlay_color);
      formData.append('login_bg_overlay_opacity', branding.login_bg_overlay_opacity);
      if (bgImageRef.current?.files[0]) formData.append('login_bg_image', bgImageRef.current.files[0]);
      if (bgVideoRef.current?.files[0]) formData.append('login_bg_video', bgVideoRef.current.files[0]);
      if (logoRef.current?.files[0]) formData.append('header_logo', logoRef.current.files[0]);
      if (faviconRef.current?.files[0]) formData.append('favicon', faviconRef.current.files[0]);
      await api.patch('/admin/site-settings/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Branding updated successfully.');
      refreshSettings();
    } catch {
      toast.error('Failed to update branding.');
    } finally {
      setBrandingSaving(false);
    }
  };

  const handleCreateDues = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/admin/dues-structure/', duesForm);
      setDuesStructures([res.data, ...duesStructures]);
      setDuesForm({ membership_category: 'fellow', year: new Date().getFullYear(), amount: '' });
      toast.success('Dues structure created.');
    } catch {
      toast.error('Failed to create dues structure.');
    }
  };

  const handleDeleteDues = async (id) => {
    if (!window.confirm('Delete this dues entry?')) return;
    try {
      await api.delete(`/admin/dues-structure/${id}/`);
      setDuesStructures(duesStructures.filter(d => d.id !== id));
      toast.success('Deleted.');
    } catch {
      toast.error('Failed to delete.');
    }
  };

  const tabs = [
    { key: 'dues', label: 'Dues Structure' },
    { key: 'branding', label: 'App Branding' },
    { key: 'audit', label: 'Audit Logs' },
  ];

  const categoryLabels = {
    fellow: 'Fellow', corporate: 'Corporate Member', associate: 'Associate',
    graduate: 'Graduate', student: 'Student', technician: 'Technician',
    probationer: 'Probationer',
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">System Settings</h1>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === t.key ? 'bg-[#0B5D1E] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B5D1E]"></div>
        </div>
      ) : activeTab === 'dues' ? (
        <>
          {/* Create Dues Form */}
          <form onSubmit={handleCreateDues} className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">Add Dues Structure</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Category</label>
                <select value={duesForm.membership_category}
                  onChange={(e) => setDuesForm({ ...duesForm, membership_category: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none">
                  {Object.entries(categoryLabels).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Year</label>
                <input type="number" required value={duesForm.year}
                  onChange={(e) => setDuesForm({ ...duesForm, year: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Amount (₦)</label>
                <input type="number" step="0.01" required value={duesForm.amount}
                  onChange={(e) => setDuesForm({ ...duesForm, amount: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
              </div>
              {canAdd(PERMISSIONS.ADD_SETTINGS) && (
                <button type="submit"
                  className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#062E10] transition">
                  Add
                </button>
              )}
            </div>
          </form>

          {/* Dues List */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left py-3 px-4">Category</th>
                  <th className="text-left py-3 px-4">Year</th>
                  <th className="text-left py-3 px-4">Amount</th>
                  <th className="text-right py-3 px-4">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {duesStructures.length === 0 ? (
                  <tr><td colSpan={4} className="py-8 text-center text-gray-500">No dues structures defined.</td></tr>
                ) : duesStructures.map(d => (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{categoryLabels[d.membership_category] || d.membership_category}</td>
                    <td className="py-3 px-4">{d.year}</td>
                    <td className="py-3 px-4">₦{parseFloat(d.amount).toLocaleString()}</td>
                    <td className="py-3 px-4 text-right">
                      {canDelete(PERMISSIONS.DELETE_SETTINGS) && (
                        <button onClick={() => handleDeleteDues(d.id)}
                          className="text-red-500 hover:text-red-700 text-sm">Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : activeTab === 'branding' ? (
        <div className="space-y-6">
          {/* Login Background */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
              Login Page Background
            </h3>

            {/* Background type selector */}
            <div className="flex gap-3 mb-5">
              {[{ key: 'color', label: 'Colour', icon: '🎨' }, { key: 'image', label: 'Image', icon: '🖼️' }, { key: 'video', label: 'Video', icon: '🎬' }].map(opt => (
                <button key={opt.key}
                  onClick={() => setBranding({ ...branding, login_bg_type: opt.key })}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border-2 text-sm font-semibold transition ${
                    branding.login_bg_type === opt.key
                      ? 'border-[#0B5D1E] bg-[#0B5D1E]/5 text-[#0B5D1E]'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}>
                  <span>{opt.icon}</span> {opt.label}
                </button>
              ))}
            </div>

            {/* Colour picker */}
            {branding.login_bg_type === 'color' && (
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-600">Background Colour:</label>
                <input type="color" value={branding.login_bg_color}
                  onChange={(e) => setBranding({ ...branding, login_bg_color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border" />
                <input type="text" value={branding.login_bg_color}
                  onChange={(e) => setBranding({ ...branding, login_bg_color: e.target.value })}
                  className="px-3 py-2 border rounded-lg text-sm w-28 font-mono focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
                <div className="w-20 h-10 rounded-lg border" style={{ background: branding.login_bg_color }} />
              </div>
            )}

            {/* Image upload */}
            {branding.login_bg_type === 'image' && (
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Background Image:</label>
                <input type="file" accept="image/*" ref={bgImageRef}
                  onChange={(e) => handleFilePreview(e.target.files[0], setBgImagePreview)}
                  className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0B5D1E] file:text-white file:font-semibold file:cursor-pointer" />
                {bgImagePreview && (
                  <div className="mt-3 relative w-64 h-40 rounded-lg overflow-hidden border group">
                    <img src={bgImagePreview} alt="Preview" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: branding.login_bg_overlay_color, opacity: branding.login_bg_overlay_opacity }} />
                    <button type="button" onClick={() => handleBrandingDelete('login_bg_image')}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity z-10" title="Remove image">
                      ✕
                    </button>
                  </div>
                )}
                {/* Overlay controls */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Background Overlay</h4>
                  <div className="flex items-center gap-4">
                    <label className="text-sm text-gray-600 w-24">Colour:</label>
                    <input type="color" value={branding.login_bg_overlay_color}
                      onChange={(e) => setBranding({ ...branding, login_bg_overlay_color: e.target.value })}
                      className="w-10 h-8 rounded cursor-pointer border" />
                    <input type="text" value={branding.login_bg_overlay_color}
                      onChange={(e) => setBranding({ ...branding, login_bg_overlay_color: e.target.value })}
                      className="px-2 py-1 border rounded-lg text-sm w-24 font-mono focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="text-sm text-gray-600 w-24">Opacity:</label>
                    <input type="range" min="0" max="1" step="0.05" value={branding.login_bg_overlay_opacity}
                      onChange={(e) => setBranding({ ...branding, login_bg_overlay_opacity: parseFloat(e.target.value) })}
                      className="flex-1 accent-[#0B5D1E]" />
                    <span className="text-sm font-mono text-gray-600 w-12 text-right">{Math.round(branding.login_bg_overlay_opacity * 100)}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Video upload */}
            {branding.login_bg_type === 'video' && (
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Background Video:</label>
                <input type="file" accept="video/mp4,video/webm" ref={bgVideoRef}
                  onChange={(e) => handleFilePreview(e.target.files[0], setBgVideoPreview)}
                  className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0B5D1E] file:text-white file:font-semibold file:cursor-pointer" />
                {bgVideoPreview && (
                  <div className="mt-3 w-64 rounded-lg overflow-hidden border relative group">
                    <video src={bgVideoPreview} className="w-full" controls muted />
                    <button type="button" onClick={() => handleBrandingDelete('login_bg_video')}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 opacity-0 group-hover:opacity-100 transition-opacity z-10" title="Remove video">
                      ✕
                    </button>
                  </div>
                )}
                {/* Overlay controls */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Background Overlay</h4>
                  <div className="flex items-center gap-4">
                    <label className="text-sm text-gray-600 w-24">Colour:</label>
                    <input type="color" value={branding.login_bg_overlay_color}
                      onChange={(e) => setBranding({ ...branding, login_bg_overlay_color: e.target.value })}
                      className="w-10 h-8 rounded cursor-pointer border" />
                    <input type="text" value={branding.login_bg_overlay_color}
                      onChange={(e) => setBranding({ ...branding, login_bg_overlay_color: e.target.value })}
                      className="px-2 py-1 border rounded-lg text-sm w-24 font-mono focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="text-sm text-gray-600 w-24">Opacity:</label>
                    <input type="range" min="0" max="1" step="0.05" value={branding.login_bg_overlay_opacity}
                      onChange={(e) => setBranding({ ...branding, login_bg_overlay_opacity: parseFloat(e.target.value) })}
                      className="flex-1 accent-[#0B5D1E]" />
                    <span className="text-sm font-mono text-gray-600 w-12 text-right">{Math.round(branding.login_bg_overlay_opacity * 100)}%</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Logo & Favicon */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/></svg>
              Logo &amp; Favicon
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Header Logo */}
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Header / Sidebar Logo</label>
                <input type="file" accept="image/*" ref={logoRef}
                  onChange={(e) => handleFilePreview(e.target.files[0], setLogoPreview)}
                  className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0B5D1E] file:text-white file:font-semibold file:cursor-pointer" />
                {logoPreview && (
                  <div className="mt-3 flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                    <img src={logoPreview} alt="Logo" className="h-12 w-auto object-contain" />
                    <span className="text-xs text-gray-500">Current logo</span>
                    <button type="button" onClick={() => handleBrandingDelete('header_logo')}
                      className="ml-auto text-red-500 hover:text-red-700 text-xs font-semibold">Remove</button>
                  </div>
                )}
              </div>

              {/* Favicon */}
              <div>
                <label className="text-sm font-medium text-gray-600 block mb-2">Favicon (Browser Tab Icon)</label>
                <input type="file" accept="image/png,image/x-icon,image/jpeg,image/svg+xml" ref={faviconRef}
                  onChange={(e) => handleFilePreview(e.target.files[0], setFaviconPreview)}
                  className="text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0B5D1E] file:text-white file:font-semibold file:cursor-pointer" />
                {faviconPreview && (
                  <div className="mt-3 flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                    <img src={faviconPreview} alt="Favicon" className="h-8 w-8 object-contain" />
                    <span className="text-xs text-gray-500">Current favicon</span>
                    <button type="button" onClick={() => handleBrandingDelete('favicon')}
                      className="ml-auto text-red-500 hover:text-red-700 text-xs font-semibold">Remove</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Save button */}
          <div className="flex justify-end">
            <button onClick={handleBrandingSave} disabled={brandingSaving}
              className="bg-[#0B5D1E] text-white px-8 py-2.5 rounded-lg font-semibold hover:bg-[#062E10] transition disabled:opacity-50 flex items-center gap-2">
              {brandingSaving && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />}
              {brandingSaving ? 'Saving...' : 'Save Branding'}
            </button>
          </div>
        </div>
      ) : (
        (() => {
          const totalPages = Math.ceil(auditLogs.length / auditPerPage);
          const paged = auditLogs.slice((auditPage - 1) * auditPerPage, auditPage * auditPerPage);
          return (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    <th className="text-left py-3 px-4">S/N</th>
                    <th className="text-left py-3 px-4">Time</th>
                    <th className="text-left py-3 px-4">User</th>
                    <th className="text-left py-3 px-4">Action</th>
                    <th className="text-left py-3 px-4">Model</th>
                    <th className="text-left py-3 px-4">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paged.length === 0 ? (
                    <tr><td colSpan={6} className="py-8 text-center text-gray-500">No audit logs yet.</td></tr>
                  ) : paged.map((log, idx) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-gray-400">{(auditPage - 1) * auditPerPage + idx + 1}</td>
                      <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 font-medium">{log.user_email || 'System'}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          log.action === 'create' ? 'bg-green-100 text-green-700' :
                          log.action === 'update' ? 'bg-blue-100 text-blue-700' :
                          log.action === 'delete' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                        }`}>{log.action}</span>
                      </td>
                      <td className="py-3 px-4">{log.model_name}</td>
                      <td className="py-3 px-4 text-gray-500 max-w-xs truncate">{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t px-4 py-3">
                  <p className="text-xs text-gray-500">
                    Showing {(auditPage - 1) * auditPerPage + 1}–{Math.min(auditPage * auditPerPage, auditLogs.length)} of {auditLogs.length} logs
                  </p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setAuditPage(1)} disabled={auditPage === 1}
                      className="px-2 py-1 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">First</button>
                    <button onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage === 1}
                      className="px-2 py-1 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - auditPage) <= 2)
                      .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) => p === '...' ? (
                        <span key={`dots-${i}`} className="px-1 text-xs text-gray-400">…</span>
                      ) : (
                        <button key={p} onClick={() => setAuditPage(p)}
                          className={`px-2.5 py-1 rounded text-xs font-semibold transition ${
                            auditPage === p ? 'bg-[#0B5D1E] text-white' : 'text-gray-600 hover:bg-gray-100'
                          }`}>{p}</button>
                      ))}
                    <button onClick={() => setAuditPage(p => Math.min(totalPages, p + 1))} disabled={auditPage === totalPages}
                      className="px-2 py-1 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                    <button onClick={() => setAuditPage(totalPages)} disabled={auditPage === totalPages}
                      className="px-2 py-1 rounded text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">Last</button>
                  </div>
                </div>
              )}
            </div>
          );
        })()
      )}
    </div>
  );
}
